/** @jsxImportSource @gongxigames/framework/core/fragments/BuilderY */
/** @jsxRuntime automatic */

import {
    FC,
    getProp,
    PointLike,
    copyPointData,
    $Container, CanBeSignal, JSX
} from '@gongxigames/framework/core/fragments/BuilderY';
import {
    FullScreenFit,
    FullScreenFitOptions,
    FullScreenFitUpdateOptions
} from '@gongxigames/framework/core/fragments/display/SafeZone/FullScreenFit';
import {Container, Point} from 'pixi.js';
import {Store} from '@gongxigames/framework/core/fragments/store';
import {slotStore, strictUiEmitter} from '@gongxigames/framework/slotV2/globals';
import {SafeZone} from '@gongxigames/framework/core/fragments/display/SafeZone/SafeZone';

export type FullScreenFitProps = {
    width: CanBeSignal<number>;
    height: CanBeSignal<number>;
    align?: CanBeSignal<PointLike>;
    offset?: CanBeSignal<PointLike>;
    safeZone?: CanBeSignal<SafeZone>;
    children: JSX.Element;
};

function updateEffect<T extends FullScreenFit>(props: FullScreenFitProps, instance: T): () => any {
    const {width, height, align, offset, safeZone} = props;

    return Store.effect(() => {
        const alignVal = getProp(align);
        const offsetVal = getProp(offset);
        const safeZoneVal = getProp(safeZone) ?? slotStore.stage_safeZoneState_game.value;

        const options: FullScreenFitUpdateOptions<Container> = {
            safeZone: safeZoneVal,
            contentSize: {
                width: getProp(width),
                height: getProp(height)
            }
        };

        if (alignVal !== undefined) {
            options.align ??= new Point();
            copyPointData(alignVal, options.align);
        }

        if (offsetVal !== undefined) {
            options.offset ??= new Point();
            copyPointData(offsetVal, options.offset);
        }

        instance.update(options);
    });
}

function createInstance(props: FullScreenFitProps, container: Container): FullScreenFit {
    const safeZone = getProp(props.safeZone) ?? slotStore.stage_safeZoneState_game.value;
    const align = getProp(props.align);
    const offset = getProp(props.offset);

    const options: FullScreenFitOptions = {
        container,
        contentSize: {
            width: getProp(props.width),
            height: getProp(props.height),
        },
        safeZone,
    };

    if (align !== undefined) {
        options.align = new Point();
        copyPointData(align, options.align);
    }

    if (offset !== undefined) {
        options.offset = new Point();
        copyPointData(offset, options.offset);
    }

    return new FullScreenFit(options);
}

export const $FullScreenFit: FC<FullScreenFitProps> = (props) => {
    const container = <$Container name='FullScreenFit'>{props.children}</$Container> as Container;
    const fullScreenFit = createInstance(props, container);
    const offUpdate = updateEffect(props, fullScreenFit);
    const offResize = strictUiEmitter.on('CORE.screen.resize', () => {
        const safeZone = getProp(props.safeZone) ?? slotStore.stage_safeZoneState_game.value;

        fullScreenFit.resize(safeZone);
    });

    fullScreenFit.container.on('destroyed', offUpdate);
    fullScreenFit.container.on('destroyed', offResize);

    return fullScreenFit.container;
};